import { useEffect, useMemo, useState } from "react";
import { http } from "../api";
import { Cake, Phone, MessageCircle, Sparkles, Send, GraduationCap, Filter, X, Users as UsersIcon, ChevronDown, ChevronUp, Check } from "lucide-react";
import { PageHeader, EmptyState } from "../shared";

const AGE_BUCKETS = [
  { id: "60-70", label: "60–70 yr" },
  { id: "70-80", label: "70–80 yr" },
  { id: "80+", label: "80+ yr" },
];
const GENDERS = ["Male", "Female", "Other"];
const BLOODS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
const INCLINATIONS = ["Supporter", "Neutral", "Undecided", "Opposition", "BJP Worker", "TMC Worker", "CPIM Worker"];
const INCOMES = ["Below 1L", "1L-3L", "3L-5L", "5L-10L", "10L+"];
const HOUSE_QUALITIES = ["Kutcha", "Semi-Pucca", "Pucca", "Apartment"];
const EDUCATIONS = ["Illiterate", "Primary", "Secondary", "HS", "Graduate", "PG", "Doctorate"];
const ORG_TYPES = ["Club", "NGO", "Temple", "Trust", "School", "Association", "Cooperative", "Political Party", "Other"];

const emptyFilter = {
  q: "", ward: "", booth: "", gender: "", inclination: "", status: "", blood_group: "",
  profession: "", education: "", income_range: "", house_quality: "",
  age_bucket: "", age_min: "", age_max: "",
  is_specially_abled: "", is_student: "", student_class: "",
  is_patient: "", disease: "", criminal_record: "", has_character_certificate: "",
  special_skill: "", special_subject: "",
  birthday_today: false, birthday_month: "",
  scheme_beneficiary_id: "", beneficiary_status: "",
  organization_id: "", organization_type: "",
};

export default function Communicate({ user, setPage }) {
  const isAdmin = user && (user.role === "Super Admin" || user.role === "Admin");
  const [filter, setFilter] = useState(emptyFilter);
  const [results, setResults] = useState([]);
  const [count, setCount] = useState(0);
  const [running, setRunning] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [advOpen, setAdvOpen] = useState(true);
  const [error, setError] = useState("");

  // Send drawer
  const [sending, setSending] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendChannel, setSendChannel] = useState("SMS");
  const [bodyEn, setBodyEn] = useState("");
  const [bodyBn, setBodyBn] = useState("");
  const [label, setLabel] = useState("Ward outreach");
  const [aiIntent, setAiIntent] = useState("");
  const [aiTone, setAiTone] = useState("warm");
  const [aiBusy, setAiBusy] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const [schemes, setSchemes] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [templates, setTemplates] = useState([]);

  // Class promotion nudge
  const [prom, setProm] = useState({ count: 0, in_annual_window: false, students: [] });
  const [promOpen, setPromOpen] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    http.get("/schemes").then((r) => setSchemes(r.data)).catch(() => {});
    http.get("/organizations").then((r) => setOrganizations(r.data)).catch(() => {});
    http.get("/voter-note-templates").then((r) => setTemplates(r.data)).catch(() => {});
    http.get("/communicate/promotion-candidates").then((r) => setProm(r.data)).catch(() => {});
  }, [isAdmin]);

  const runFilter = async (override) => {
    setRunning(true); setError("");
    try {
      // Defensive: if a DOM event slipped in, ignore it.
      const src = (override && !override.nativeEvent && !override.target) ? override : filter;
      const payload = { ...src };
      // Cast numeric / bool fields
      if (payload.ward === "") delete payload.ward; else payload.ward = Number(payload.ward);
      if (payload.age_min === "") delete payload.age_min; else payload.age_min = Number(payload.age_min);
      if (payload.age_max === "") delete payload.age_max; else payload.age_max = Number(payload.age_max);
      if (payload.birthday_month === "") delete payload.birthday_month; else payload.birthday_month = Number(payload.birthday_month);
      // Empty-string strings — drop them so server treats as unset
      for (const k of Object.keys(payload)) {
        if (payload[k] === "" || payload[k] === undefined) delete payload[k];
      }
      // Bool tri-state ("", "yes", "no") -> true / false
      for (const k of ["is_specially_abled","is_student","is_patient","criminal_record","has_character_certificate"]) {
        if (payload[k] === "yes") payload[k] = true;
        else if (payload[k] === "no") payload[k] = false;
        else delete payload[k];
      }
      const r = await http.post("/communicate/filter", payload);
      setResults(r.data.results || []);
      setCount(r.data.count || 0);
      setSelected(new Set());
      setSelectAll(false);
    } catch (e) {
      setError(e.response?.data?.detail || "Filter failed");
    } finally { setRunning(false); }
  };

  const useBirthdayToday = () => {
    const f = { ...emptyFilter, birthday_today: true };
    setFilter(f);
    runFilter(f);
  };

  const toggleOne = (id) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
    setSelectAll(n.size === results.length && results.length > 0);
  };
  const toggleAll = () => {
    if (selectAll) { setSelected(new Set()); setSelectAll(false); }
    else { setSelected(new Set(results.map((r) => r.contact_id))); setSelectAll(true); }
  };

  const callOne = async (c) => {
    try { await http.post(`/communicate/call/${c.contact_id}`); } catch { /* noop */ }
    if (c.mobile) window.location.href = `tel:${c.mobile}`;
  };

  const aiSuggest = async () => {
    if (!aiIntent.trim()) return;
    setAiBusy(true);
    try {
      const r = await http.post("/communicate/ai-suggest", { intent: aiIntent.trim(), tone: aiTone });
      setBodyEn(r.data.en || "");
      setBodyBn(r.data.bn || "");
    } catch (e) { setError(e.response?.data?.detail || "AI suggest failed"); }
    finally { setAiBusy(false); }
  };

  const doSend = async () => {
    setSending(true); setError("");
    try {
      const r = await http.post("/communicate/send", {
        contact_ids: [...selected],
        channel: sendChannel, body_en: bodyEn, body_bn: bodyBn, label,
      });
      setSendResult(r.data);
    } catch (e) { setError(e.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  const applyTemplate = (t) => {
    setBodyEn((cur) => cur ? cur : t.text);
    setLabel(t.category || "Communicate");
  };

  const useExamPreset = () => {
    setSendOpen(true);
    setLabel("Exam follow-up");
    setBodyEn("Hi {name}, hope your board exams went well. Our team is here if you need any post-exam support. — Team Ward 7");
    setBodyBn("প্রিয় {name}, আশা করি তোমার বোর্ড পরীক্ষা ভাল হয়েছে। কোনো সাহায্য দরকার হলে আমাদের ওয়ার্ড ৭ দল সাহায্যে আছে।");
    const f = { ...emptyFilter, is_student: "yes" };
    setFilter(f);
    runFilter(f);
  };

  const openSendDrawer = () => {
    if (selected.size === 0) { window.alert("Select at least one recipient"); return; }
    setSendOpen(true); setSendResult(null);
  };

  const isExamMonth = useMemo(() => {
    const m = new Date().getMonth() + 1;
    return m >= 3 && m <= 5;
  }, []);

  if (!isAdmin) {
    return (
      <>
        <PageHeader eyebrow="COMMUNICATE" title="Communicate" subtitle="Admin & Super Admin only" />
        <EmptyState icon={Send} title="Admin access required" hint="Ask an admin to open the Communicate console." />
      </>
    );
  }

  return (
    <>
      <PageHeader eyebrow="OUTREACH · MULTI-FILTER" title="Communicate"
        subtitle="Slice the ward by any dimension and reach the right voters — one call at a time or in bulk, in English + Bengali."
        actions={
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button className="cm-birthday" data-testid="cm-birthday-today" onClick={useBirthdayToday}>
              <Cake size={14} /> Birthday today
            </button>
            {isExamMonth && (
              <button className="outline" data-testid="cm-exam-preset" onClick={useExamPreset}>
                <GraduationCap size={13} /> Exam follow-up (bilingual)
              </button>
            )}
            {(prom.count > 0 || prom.in_annual_window) && (
              <button className="outline" data-testid="cm-promote-open" onClick={() => setPromOpen(true)}>
                <GraduationCap size={13} /> {prom.in_annual_window ? "Annual class promotion" : `Promote ${prom.count} student${prom.count === 1 ? "" : "s"}`}
              </button>
            )}
          </div>
        } />

      {/* Filter panel */}
      <section className="panel cm-filter-panel" data-testid="cm-filter-panel">
        <div className="cm-filter-head" onClick={() => setAdvOpen((s) => !s)}>
          <b><Filter size={14} /> Filters</b>
          <span>{advOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</span>
        </div>
        {advOpen && (
          <div className="cm-filter-grid" data-testid="cm-filter-grid">
            <label>Search<input data-testid="cm-q" placeholder="Name / mobile / EPIC" value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })} /></label>
            <label>Ward<input data-testid="cm-ward" type="number" value={filter.ward} onChange={(e) => setFilter({ ...filter, ward: e.target.value })} /></label>
            <label>Booth<input data-testid="cm-booth" value={filter.booth} onChange={(e) => setFilter({ ...filter, booth: e.target.value })} /></label>
            <label>Gender<select data-testid="cm-gender" value={filter.gender} onChange={(e) => setFilter({ ...filter, gender: e.target.value })}><option value="">Any</option>{GENDERS.map((g) => <option key={g}>{g}</option>)}</select></label>
            <label>Status<select data-testid="cm-status" value={filter.status || ""} onChange={(e) => setFilter({ ...filter, status: e.target.value })}><option value="">Any</option><option>Verified</option><option>Incomplete</option></select></label>
            <label>Inclination<select data-testid="cm-inclination" value={filter.inclination} onChange={(e) => setFilter({ ...filter, inclination: e.target.value })}><option value="">Any</option>{INCLINATIONS.map((i) => <option key={i}>{i}</option>)}</select></label>
            <label>Blood group<select data-testid="cm-blood" value={filter.blood_group} onChange={(e) => setFilter({ ...filter, blood_group: e.target.value })}><option value="">Any</option>{BLOODS.map((b) => <option key={b}>{b}</option>)}</select></label>
            <label>Profession<input data-testid="cm-profession" value={filter.profession} onChange={(e) => setFilter({ ...filter, profession: e.target.value })} placeholder="Teacher, Farmer…" /></label>
            <label>Education<select data-testid="cm-education" value={filter.education} onChange={(e) => setFilter({ ...filter, education: e.target.value })}><option value="">Any</option>{EDUCATIONS.map((e) => <option key={e}>{e}</option>)}</select></label>
            <label>Income range<select data-testid="cm-income" value={filter.income_range} onChange={(e) => setFilter({ ...filter, income_range: e.target.value })}><option value="">Any</option>{INCOMES.map((v) => <option key={v}>{v}</option>)}</select></label>
            <label>House quality<select data-testid="cm-house" value={filter.house_quality} onChange={(e) => setFilter({ ...filter, house_quality: e.target.value })}><option value="">Any</option>{HOUSE_QUALITIES.map((v) => <option key={v}>{v}</option>)}</select></label>
            <label>Special skill<input data-testid="cm-skill" value={filter.special_skill} onChange={(e) => setFilter({ ...filter, special_skill: e.target.value })} placeholder="Driving, Tailoring…" /></label>

            <label>Age bracket
              <select data-testid="cm-agebucket" value={filter.age_bucket} onChange={(e) => setFilter({ ...filter, age_bucket: e.target.value })}>
                <option value="">Any</option>{AGE_BUCKETS.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
              </select>
            </label>
            <label>Age min<input data-testid="cm-agemin" type="number" value={filter.age_min} onChange={(e) => setFilter({ ...filter, age_min: e.target.value })} /></label>
            <label>Age max<input data-testid="cm-agemax" type="number" value={filter.age_max} onChange={(e) => setFilter({ ...filter, age_max: e.target.value })} /></label>
            <label>Birthday month<select data-testid="cm-bday-month" value={filter.birthday_month} onChange={(e) => setFilter({ ...filter, birthday_month: e.target.value })}><option value="">Any</option>{[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => <option key={m} value={m}>{new Date(2020, m-1, 1).toLocaleString(undefined, {month:"long"})}</option>)}</select></label>

            <label>Specially-abled<select data-testid="cm-abled" value={filter.is_specially_abled} onChange={(e) => setFilter({ ...filter, is_specially_abled: e.target.value })}><option value="">Any</option><option value="yes">Yes</option><option value="no">No</option></select></label>
            <label>Student<select data-testid="cm-student" value={filter.is_student} onChange={(e) => setFilter({ ...filter, is_student: e.target.value })}><option value="">Any</option><option value="yes">Yes</option><option value="no">No</option></select></label>
            {filter.is_student === "yes" && (
              <label>Student class<input data-testid="cm-class" value={filter.student_class} onChange={(e) => setFilter({ ...filter, student_class: e.target.value })} placeholder="Class 10" /></label>
            )}
            {filter.is_student === "yes" && (
              <label>Special subject<input data-testid="cm-subject" value={filter.special_subject} onChange={(e) => setFilter({ ...filter, special_subject: e.target.value })} placeholder="Physics" /></label>
            )}
            <label>Patient<select data-testid="cm-patient" value={filter.is_patient} onChange={(e) => setFilter({ ...filter, is_patient: e.target.value })}><option value="">Any</option><option value="yes">Yes</option><option value="no">No</option></select></label>
            {filter.is_patient === "yes" && (
              <label>Disease<input data-testid="cm-disease" value={filter.disease} onChange={(e) => setFilter({ ...filter, disease: e.target.value })} placeholder="Diabetes" /></label>
            )}
            <label>Criminal record<select data-testid="cm-criminal" value={filter.criminal_record} onChange={(e) => setFilter({ ...filter, criminal_record: e.target.value })}><option value="">Any</option><option value="yes">Yes</option><option value="no">No</option></select></label>
            <label>Character certificate<select data-testid="cm-cc" value={filter.has_character_certificate} onChange={(e) => setFilter({ ...filter, has_character_certificate: e.target.value })}><option value="">Any</option><option value="yes">Yes</option><option value="no">No</option></select></label>

            <label>Scheme beneficiary<select data-testid="cm-scheme" value={filter.scheme_beneficiary_id} onChange={(e) => setFilter({ ...filter, scheme_beneficiary_id: e.target.value })}><option value="">Any scheme</option>{schemes.map((s) => <option key={s.scheme_id} value={s.scheme_id}>{s.name}</option>)}</select></label>
            {filter.scheme_beneficiary_id && (
              <label>Beneficiary status<select data-testid="cm-bstatus" value={filter.beneficiary_status} onChange={(e) => setFilter({ ...filter, beneficiary_status: e.target.value })}><option value="">Any</option><option>Approved</option><option>Rejected</option><option>Past Beneficiary</option><option>Under Inquiry</option></select></label>
            )}
            <label>Organization<select data-testid="cm-org" value={filter.organization_id} onChange={(e) => setFilter({ ...filter, organization_id: e.target.value })}><option value="">Any</option>{organizations.map((o) => <option key={o.organization_id} value={o.organization_id}>{o.name}</option>)}</select></label>
            <label>Organization type<select data-testid="cm-orgtype" value={filter.organization_type} onChange={(e) => setFilter({ ...filter, organization_type: e.target.value })}><option value="">Any</option>{ORG_TYPES.map((t) => <option key={t}>{t}</option>)}</select></label>
          </div>
        )}
        <div className="cm-filter-actions">
          <button className="outline" data-testid="cm-reset" onClick={() => { setFilter(emptyFilter); setResults([]); setCount(0); }}><X size={13} /> Reset</button>
          <button className="primary" data-testid="cm-run" onClick={() => runFilter()} disabled={running}>{running ? "Filtering…" : "Run filter"}</button>
        </div>
        {error && <p className="hint" style={{ color: "#b91c1c" }}>{error}</p>}
      </section>

      {/* Results grid */}
      <section className="panel" style={{ marginTop: 14 }}>
        <div className="cm-results-head" data-testid="cm-results-head">
          <div>
            <b><UsersIcon size={14} /> {count} match{count === 1 ? "" : "es"}</b>
            {selected.size > 0 && <small style={{ marginLeft: 8 }}>· {selected.size} selected</small>}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="outline" data-testid="cm-toggle-all" onClick={toggleAll} disabled={results.length === 0}>{selectAll ? "Clear" : "Select all"}</button>
            <button className="primary" data-testid="cm-send-open" onClick={openSendDrawer} disabled={selected.size === 0}><Send size={13} /> Send bilingual</button>
          </div>
        </div>
        {results.length === 0 ? (
          <p className="hint" style={{ padding: 24, textAlign: "center" }}>Set filters and hit &ldquo;Run filter&rdquo; to see matching voters.</p>
        ) : (
          <div className="cm-results" data-testid="cm-results">
            {results.map((c) => (
              <div key={c.contact_id} className="cm-row" data-testid={`cm-row-${c.contact_id}`}>
                <input type="checkbox" data-testid={`cm-check-${c.contact_id}`} checked={selected.has(c.contact_id)} onChange={() => toggleOne(c.contact_id)} />
                <div className="cm-body">
                  <b>{c.name}</b>
                  <small>{c.gender} · {c.age_computed ?? c.age ?? "—"}y · Booth {c.booth} · {c.inclination}{c.is_student ? ` · ${c.student_class || "Student"}` : ""}{c.is_patient ? ` · Patient` : ""}</small>
                </div>
                <div className="cm-mobile">{c.mobile || <em style={{ color: "#a2b3ac" }}>no mobile</em>}</div>
                <div className="cm-actions">
                  <button className="outline" data-testid={`cm-call-${c.contact_id}`} disabled={!c.mobile} onClick={() => callOne(c)}><Phone size={12} /> Call</button>
                  {c.whatsapp && <a className="outline" data-testid={`cm-wa-${c.contact_id}`} href={`https://wa.me/${c.whatsapp.replace(/\D/g,"")}`} target="_blank" rel="noreferrer"><MessageCircle size={12} /> WA</a>}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Send drawer */}
      {sendOpen && (
        <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) { setSendOpen(false); setSendResult(null); } }}>
          <div className="cm-drawer" data-testid="cm-send-drawer">
            <div className="cm-drawer-head">
              <b><Send size={14} /> Send bilingual — {selected.size} recipient{selected.size === 1 ? "" : "s"}</b>
              <button className="icon-btn" data-testid="cm-drawer-close" onClick={() => { setSendOpen(false); setSendResult(null); }}><X size={14} /></button>
            </div>
            {sendResult ? (
              <div className="cm-send-result" data-testid="cm-send-result">
                <Check size={22} color="#0f766e" />
                <b>Sent to {sendResult.sent} recipient{sendResult.sent === 1 ? "" : "s"}</b>
                {sendResult.skipped > 0 && <small>{sendResult.skipped} skipped (no mobile).</small>}
                <small>Provider: {sendResult.provider}</small>
                <button className="primary" data-testid="cm-send-done" onClick={() => { setSendOpen(false); setSendResult(null); }}>Done</button>
              </div>
            ) : (
              <>
                <div className="cm-drawer-row">
                  <label>Channel
                    <select data-testid="cm-send-channel" value={sendChannel} onChange={(e) => setSendChannel(e.target.value)}>
                      <option>SMS</option><option>WhatsApp</option>
                    </select>
                  </label>
                  <label>Label<input data-testid="cm-send-label" value={label} onChange={(e) => setLabel(e.target.value)} /></label>
                </div>
                <label className="cm-drawer-full">English body (use {"{name}"} for personalisation)
                  <textarea rows={3} data-testid="cm-send-en" value={bodyEn} onChange={(e) => setBodyEn(e.target.value)} placeholder="Hello {name}, …" />
                </label>
                <label className="cm-drawer-full">Bengali body (বাংলা)
                  <textarea rows={3} data-testid="cm-send-bn" value={bodyBn} onChange={(e) => setBodyBn(e.target.value)} placeholder="নমস্কার {name}, …" />
                </label>
                <div className="cm-drawer-row cm-ai" data-testid="cm-ai">
                  <label>Suggest bilingual
                    <input data-testid="cm-ai-intent" placeholder="e.g. Birthday wish, Exam follow-up" value={aiIntent} onChange={(e) => setAiIntent(e.target.value)} />
                  </label>
                  <label>Tone
                    <select data-testid="cm-ai-tone" value={aiTone} onChange={(e) => setAiTone(e.target.value)}>
                      <option value="warm">Warm</option><option value="formal">Formal</option><option value="urgent">Urgent</option>
                    </select>
                  </label>
                  <button className="outline" data-testid="cm-ai-run" onClick={aiSuggest} disabled={aiBusy || !aiIntent.trim()}><Sparkles size={13} /> {aiBusy ? "Suggesting…" : "AI suggest"}</button>
                </div>
                {templates.length > 0 && (
                  <div className="cm-drawer-templates" data-testid="cm-drawer-templates">
                    <small>Or drop-in a template chip:</small>
                    <div className="cm-tpl-strip">
                      {templates.slice(0, 8).map((t) => (
                        <button key={t.template_id} className="cm-tpl-chip" data-testid={`cm-tpl-${t.template_id}`} onClick={() => applyTemplate(t)}>
                          {t.text.length > 32 ? `${t.text.slice(0, 32)}…` : t.text}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="cm-drawer-actions">
                  <button className="outline" data-testid="cm-send-cancel" onClick={() => setSendOpen(false)}>Cancel</button>
                  <button className="primary" data-testid="cm-send-do" disabled={sending || (!bodyEn && !bodyBn)} onClick={doSend}><Send size={13} /> {sending ? "Sending…" : "Send now"}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Class promotion nudge */}
      {promOpen && (
        <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setPromOpen(false); }}>
          <PromotionPanel prom={prom} setProm={setProm} onClose={() => { setPromOpen(false); http.get("/communicate/promotion-candidates").then((r) => setProm(r.data)).catch(() => {}); }} />
        </div>
      )}
    </>
  );
}

function PromotionPanel({ prom, setProm, onClose }) {
  const [busy, setBusy] = useState({});
  const [local, setLocal] = useState(prom.students || []);
  useEffect(() => { setLocal(prom.students || []); }, [prom]);
  const promote = async (s, cls) => {
    setBusy((b) => ({ ...b, [s.contact_id]: true }));
    try {
      await http.post(`/communicate/promote-class/${s.contact_id}`, { new_class: cls });
      setLocal((cur) => cur.filter((x) => x.contact_id !== s.contact_id));
      setProm((p) => ({ ...p, count: Math.max(0, (p.count || 1) - 1), students: (p.students || []).filter((x) => x.contact_id !== s.contact_id) }));
    } catch (e) { window.alert(e.response?.data?.detail || "Promote failed"); }
    finally { setBusy((b) => ({ ...b, [s.contact_id]: false })); }
  };
  const skip = (s) => setLocal((cur) => cur.filter((x) => x.contact_id !== s.contact_id));
  return (
    <div className="cm-drawer" data-testid="cm-promote-panel">
      <div className="cm-drawer-head">
        <b><GraduationCap size={14} /> Class promotion — {local.length} student{local.length === 1 ? "" : "s"}</b>
        <button className="icon-btn" data-testid="cm-promote-close" onClick={onClose}><X size={14} /></button>
      </div>
      {prom.in_annual_window && <p className="hint" style={{ color: "#a06508" }}>📅 It&rsquo;s June — annual promotion window. Please confirm each student&rsquo;s next class.</p>}
      {local.length === 0 ? (
        <p className="hint" style={{ textAlign: "center", padding: 24 }}>No pending promotions.</p>
      ) : (
        <ul className="cm-promote-list" data-testid="cm-promote-list">
          {local.map((s) => (
            <li key={s.contact_id} data-testid={`cm-promote-${s.contact_id}`}>
              <div>
                <b>{s.name}</b>
                <small>Current: {s.current_class || "—"} → Suggested: <b>{s.suggested_class}</b></small>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button className="primary" data-testid={`cm-promote-confirm-${s.contact_id}`} disabled={busy[s.contact_id]} onClick={() => promote(s, s.suggested_class)}>Confirm</button>
                <button className="outline" data-testid={`cm-promote-skip-${s.contact_id}`} onClick={() => skip(s)}>Skip</button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
